import { useState } from 'react';
import { isApiError } from '@/services/api';
import { Alert, Button, Checkbox, Modal, SearchBar } from '@shared/components';
import { useGroupAttachableUsersQuery } from '../hooks/useGroupAttachableUsersQuery';
import { useAttachUsersMutation } from '../hooks/useAttachUsersMutation';

const MAX_ATTACH = 200;

interface Props {
  groupId: string;
  attachedUserIds: string[];
  onClose: () => void;
}

interface DefaultPermissions {
  can_quote: boolean;
  can_invoice: boolean;
  can_sell: boolean;
}

// attachedUserIds is accepted for API compatibility; backend excludes already-attached users from results.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function AttachUsersModal({ groupId, attachedUserIds: _attachedUserIds, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [attachAll, setAttachAll] = useState(false);
  const [defaults, setDefaults] = useState<DefaultPermissions>({ can_quote: false, can_invoice: false, can_sell: false });
  const [defaultsTouched, setDefaultsTouched] = useState(false);
  const [outOfScopeIds, setOutOfScopeIds] = useState<string[]>([]);

  // Browse query: normal paginated view used when attachAll is false.
  const browseQuery = useGroupAttachableUsersQuery({
    groupId,
    search: search.length >= 2 ? search : undefined,
    page: 1,
    per_page: 25,
  });

  // All-candidates query: deferred until attachAll is ticked so the 200-row
  // fetch is genuinely one-shot and is not sent on every modal open.
  const allQuery = useGroupAttachableUsersQuery({
    groupId,
    search: search.length >= 2 ? search : undefined,
    page: 1,
    per_page: MAX_ATTACH,
    enabled: attachAll,
  });

  const attachMutation = useAttachUsersMutation(groupId);

  const total = browseQuery.data?.pagination.total ?? 0;
  const tooManyForAll = total > MAX_ATTACH;

  const displayUsers = attachAll
    ? (allQuery.data?.data ?? [])
    : (browseQuery.data?.data ?? []);

  const allCandidateIds = allQuery.data?.data.map((u) => u.id) ?? [];

  // When attachAll is true, submitIds is the full candidate set.
  // When false, it's the per-row selection.
  const submitIds = attachAll ? allCandidateIds : selected;

  const submitLabel = attachAll
    ? `Attach All (${allCandidateIds.length})`
    : `Attach (${selected.length})`;

  const isLoading = browseQuery.isLoading || (attachAll && allQuery.isLoading);

  const isEmpty = !isLoading && total === 0;

  const toggle = (id: string) => {
    if (attachAll) return;
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleDefault = (key: keyof DefaultPermissions, value: boolean) => {
    setDefaults((prev) => ({ ...prev, [key]: value }));
    setDefaultsTouched(true);
  };

  const handleAttachAllChange = (checked: boolean) => {
    setAttachAll(checked);
    if (!checked) {
      setSelected([]);
    }
  };

  const canSubmit = submitIds.length > 0 && !attachMutation.isPending;

  const handleConfirm = () => {
    if (!canSubmit) return;
    setOutOfScopeIds([]);
    const payload = {
      user_ids: submitIds,
      ...(defaultsTouched ? { default_permissions: defaults } : {}),
    };
    attachMutation.mutate(payload, {
      onSuccess: () => {
        setSelected([]);
        setAttachAll(false);
        onClose();
      },
      // The candidate endpoint pre-filters to eligible dealers; security-service
      // still 422s on ineligible ids (e.g. a dealer detached mid-session).
      onError: (err) => {
        if (!isApiError(err) || err.status !== 422) return;
        const body = err.data as { out_of_scope_ids?: unknown } | undefined;
        if (Array.isArray(body?.out_of_scope_ids)) {
          setOutOfScopeIds(body.out_of_scope_ids as string[]);
        }
      },
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      className="max-w-lg mx-4 max-h-screen overflow-y-auto"
      ariaLabel="Attach Users"
    >
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Attach Users</h2>

        {/* Attach-all checkbox */}
        <div className="space-y-1">
          <Checkbox
            id="attach-all"
            label="Attach all Users to this Security Group *"
            checked={attachAll}
            onChange={handleAttachAllChange}
            disabled={tooManyForAll || isEmpty || attachMutation.isPending}
          />
          {tooManyForAll && (
            <p className="text-xs text-mp-muted pl-6">
              Too many candidates — narrow with the search box first.
            </p>
          )}
          <p className="text-xs text-mp-warning pl-6">
            * Please note that there is no easy way to un-attach Users en masse — so be careful when using this option
          </p>
        </div>

        <SearchBar
          searchTerm={search}
          setSearchTerm={setSearch}
          placeholder="Search users"
        />

        <div className="space-y-2 max-h-48 overflow-y-auto">
          {isLoading && <p className="text-sm text-mp-muted">Loading...</p>}
          {!isLoading && isEmpty && (
            <p className="text-sm text-mp-muted">
              All eligible users are already attached, or no users are homed at a dealer eligible for this security group.
            </p>
          )}
          {!isLoading && !isEmpty && displayUsers.map((user) => {
            const isChecked = attachAll || selected.includes(user.id);
            return (
              <div key={user.id} className="flex items-center gap-2">
                <Checkbox
                  id={`user-${user.id}`}
                  checked={isChecked}
                  onChange={() => toggle(user.id)}
                  disabled={attachAll || attachMutation.isPending}
                  ariaLabel={user.name}
                />
                <span className="text-sm">
                  {user.name} <span className="text-mp-muted">({user.email})</span>
                </span>
              </div>
            );
          })}
        </div>

        {!attachAll && (
          <p className="text-xs text-mp-muted">
            Selected: {selected.length} / {MAX_ATTACH}
          </p>
        )}

        <div className="border-t border-mp-border pt-4 space-y-2">
          <h3 className="text-sm font-semibold">Default Permissions <span className="text-mp-muted font-normal">(optional)</span></h3>
          <div className="flex gap-4">
            <Checkbox
              id="default-can-quote"
              label="Can Quote"
              checked={defaults.can_quote}
              onChange={(v) => toggleDefault('can_quote', v)}
              disabled={attachMutation.isPending}
            />
            <Checkbox
              id="default-can-invoice"
              label="Can Invoice"
              checked={defaults.can_invoice}
              onChange={(v) => toggleDefault('can_invoice', v)}
              disabled={attachMutation.isPending}
            />
            <Checkbox
              id="default-can-sell"
              label="Can Sell"
              checked={defaults.can_sell}
              onChange={(v) => toggleDefault('can_sell', v)}
              disabled={attachMutation.isPending}
            />
          </div>
        </div>

        {outOfScopeIds.length > 0 && (
          <Alert variant="error" className="text-xs">
            <div>
              <p className="font-semibold">
                {outOfScopeIds.length} user{outOfScopeIds.length > 1 ? 's are' : ' is'} not homed at a dealer eligible for this security group and could not be attached.
              </p>
              <p className="mt-1">IDs: {outOfScopeIds.join(', ')}</p>
            </div>
          </Alert>
        )}

        {attachMutation.isError && outOfScopeIds.length === 0 && (
          <p className="text-mp-error text-xs">Failed to attach users. Please try again.</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={attachMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {attachMutation.isPending ? 'Attaching...' : submitLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
